import { useEffect, useRef, useState } from "react";
import { api, apiError, formatApiError, resolveImg, refreshFileToken, uploadFile, variantWidth } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { currencySymbol, formatCZK } from "@/lib/format";
import { MapView } from "@/components/MapView";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Upload, Check, Image as ImageIcon, Trash2, AlertTriangle, Building2, MapPin, Wallet, Users } from "lucide-react";
import { toast } from "sonner";
import { ask } from "@/lib/confirm";

// Starší zakázky mají adresu jen v jednom poli `address`. Než ji uživatel poprvé
// uloží po částech, rozparsuje se tímhle — ať nezačíná s prázdným formulářem.
// Co se nerozparsuje (např. „Korunní 810/25, Praha 2 – Vinohrady"), spadne celé
// do Ulice a uživatel to dorovná ručně.
export const splitAddress = (a) => {
  const s = (a || "").trim();
  const m = s.match(/^(.*?),\s*(\d{3}\s?\d{2})\s+(.*)$/);
  return m ? { street: m[1].trim(), zip: m[2].trim(), city: m[3].trim() } : { street: s, zip: "", city: "" };
};
// Jednořádková podoba pro dotaz na geokódování a pro popisek pod mapou.
// Do databáze ji skládá server (compose_address) — tohle je jen zobrazení.
export const joinAddress = ({ street, zip, city }) =>
  [(street || "").trim(), [(zip || "").trim(), (city || "").trim()].filter(Boolean).join(" ")]
    .filter(Boolean).join(", ");

// Na stavbě nikdo nepíše čísla „správně" — bere mezery jako oddělovač tisíců
// i desetinnou čárku. Nativní type="number" by takový vstup zahodil.
const parseNum = (v) => {
  if (v === "" || v === null || v === undefined) return null;
  const n = Number(String(v).replace(/[\s ]/g, "").replace(",", "."));
  return isNaN(n) ? null : n;
};
const dayWord = (d) => (d === 1 ? "den" : d >= 2 && d <= 4 ? "dny" : "dní");
const STATUSES = ["Poptávka", "Nabídka", "Smlouva podepsána", "V realizaci", "Dokončeno", "Fakturováno", "Záruka"];

// Vzhled 1:1 se sdíleným FormSection (karta bg-secondary/60 + brand ikona);
// navíc umí `action` (tlačítko Najít GPS) a pole uvnitř dostávají bg-card.
function Sec({ icon: Icon, title, action, children, testid }) {
  return (
    <section data-testid={testid} className="bg-secondary/60 border border-border rounded-[14px] p-4 space-y-3.5 min-w-0 [&_input]:bg-card [&_textarea]:bg-card [&_[role=combobox]]:bg-card [&_input]:placeholder:text-muted-foreground/55 [&_textarea]:placeholder:text-muted-foreground/55">
      <div className="flex items-center justify-between gap-3">
        <h3 className="flex items-center gap-2 font-heading font-extrabold text-[12px] uppercase tracking-[0.07em]">
          {Icon && <Icon size={16} className="text-brand shrink-0" />}
          {title}
        </h3>
        {action}
      </div>
      {children}
    </section>
  );
}

export function ProjectEditDialog({ open, onOpenChange, projectId, proj, team, investors, buildPayload, onSaved, onDeleted, mode = "edit", onCreated, createStatus }) {
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [geoBusy, setGeoBusy] = useState(false);
  const [geoHits, setGeoHits] = useState([]);
  const [geoState, setGeoState] = useState("idle");   // idle | ok | error
  const [imgReady, setImgReady] = useState(false);
  const [mapPos, setMapPos] = useState(null);
  const [addTeam, setAddTeam] = useState(false);       // 169/C3 — přidat celý tým firmy do nové zakázky
  const [eligibleIds, setEligibleIds] = useState([]);   // ID způsobilých členů (dělníci/subdodavatelé/stavbyvedoucí)
  const { user, company } = useAuth();
  const vatWord = company?.vat_payer === false ? "s DPH" : "bez DPH";
  // Smazání zakázky — dvojí ověření: výpis dat s počty + opsání názvu.
  const [delOpen, setDelOpen] = useState(false);
  const [delInfo, setDelInfo] = useState(null);   // { name, trashDays, counts }
  const [delName, setDelName] = useState("");
  const [delBusy, setDelBusy] = useState(false);
  const openDelete = async () => {
    setDelName("");
    try {
      const { data } = await api.get(`/projects/${projectId}/delete-preview`);
      setDelInfo(data); setDelOpen(true);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const confirmDelete = async () => {
    setDelBusy(true);
    try {
      await api.delete(`/projects/${projectId}`);
      toast.success("Zakázka je v koši. Do 7 dnů ji jde obnovit na stránce Zakázky.");
      setDelOpen(false); onOpenChange(false); onDeleted?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setDelBusy(false); }
  };
  const initial = useRef("");
  const coverRef = useRef(null);
  // 232: GPS se hledá samo po dopsání adresy. `last` = naposledy hledaná adresa,
  // `manual` = souřadnice psané ručně (ty automatika nepřepíše), `start` = adresa
  // při otevření (k ní uložené souřadnice necháváme).
  const geoRef = useRef({ last: "", manual: false, start: "" });

  // Otevření = čerstvý snímek zakázky do formuláře + otisk pro hlídání změn.
  // V režimu create začíná formulář prázdný (stav Poptávka, z tabule 304 fáze sloupce).
  useEffect(() => {
    if (!open) return;
    if (mode === "create") {
      const init = { name: "", code: "", street: "", zip: "", city: "", lat: "", lng: "",
        contract_price: "", planned_budget: "", area_m2: "", start_date: "", end_date: "",
        description: "", cover_photo: null, site_manager_id: "", investor_id: "", status: createStatus || "Poptávka" };
      initial.current = JSON.stringify(init);
      setF(init);
      geoRef.current = { last: "", manual: false, start: "" };
      setGeoHits([]); setGeoState("idle"); setSaved(false);
      setAddTeam(company?.auto_team_new_projects || false);
      api.get("/team").then((r) => setEligibleIds(
        (r.data || []).filter((u) => ["WORKER", "SUBCONTRACTOR", "MANAGER"].includes(u.role) && u.status !== "inactive").map((u) => u.id)
      )).catch(() => setEligibleIds([]));
      setImgReady(false);
      refreshFileToken().then(() => setImgReady(true)).catch(() => setImgReady(true));
      return;
    }
    if (!proj) return;
    // Nové zakázky mají části uložené; u starých se rozparsuje `address`.
    const a = (proj.street || proj.zip || proj.city)
      ? { street: proj.street || "", zip: proj.zip || "", city: proj.city || "" }
      : splitAddress(proj.address);
    const init = {
      name: proj.name || "", code: proj.code || "",
      street: a.street, zip: a.zip, city: a.city,
      lat: proj.lat ?? "", lng: proj.lng ?? "",
      contract_price: proj.contract_price ?? "", planned_budget: proj.planned_budget ?? "",
      area_m2: proj.area_m2 ?? "",
      start_date: (proj.start_date || "").slice(0, 10), end_date: (proj.end_date || "").slice(0, 10),
      description: proj.description || "", cover_photo: proj.cover_photo || null,
      site_manager_id: proj.site_manager_id || "", investor_id: proj.investor_id || "",
    };
    initial.current = JSON.stringify(init);
    setF(init);
    geoRef.current = { last: "", manual: false, start: joinAddress(init) };
    setGeoHits([]); setGeoState("idle"); setSaved(false);
    // Cover leží v chráněném úložišti — bez podepsané URL by se náhled nenačetl.
    setImgReady(false);
    refreshFileToken().then(() => setImgReady(true)).catch(() => setImgReady(true));
    // Schválně jen na `open`: po uložení se v rodiči překreslí `proj` a formulář
    // by se uprostřed zavírací animace přenačetl (a zhaslo by zelené „Uloženo").
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, proj?.id]);

  // Mapa se překresluje s prodlevou — jinak by Leaflet skákal po každém znaku.
  useEffect(() => {
    const la = parseNum(f?.lat), ln = parseNum(f?.lng);
    const ok = la !== null && ln !== null && Math.abs(la) <= 90 && Math.abs(ln) <= 180;
    const t = setTimeout(() => setMapPos(ok ? { lat: la, lng: ln } : null), 400);
    return () => clearTimeout(t);
  }, [f?.lat, f?.lng]);

  // Adresa → GPS. auto = spuštěno samo: první shodu rovnou použije (další nabídne
  // k výběru) a chybu jen napíše pod pole, bez vyskakovací hlášky.
  const geocode = async (line, auto) => {
    geoRef.current.last = line;
    if (!auto) geoRef.current.manual = false;
    setGeoBusy(true); if (!auto) setGeoHits([]);
    try {
      const { data } = await api.get("/projects/geocode", { params: { q: line } });
      if (geoRef.current.last !== line) return;          // mezitím se adresa změnila
      if (!data.length) { setGeoHits([]); setGeoState("error"); return; }
      if (auto || data.length === 1) {
        setF((p) => ({ ...p, lat: data[0].lat, lng: data[0].lng }));
        setGeoHits(data.length > 1 ? data : []);
        setGeoState(auto ? "auto" : "ok");
        return;
      }
      setGeoHits(data);   // ruční hledání s víc kandidáty — ať si uživatel vybere, netipujeme za něj
    } catch (e) {
      setGeoState("error");
      if (!auto) toast.error(apiError(e));
    } finally { setGeoBusy(false); }
  };

  // Hledá samo chvíli po dopsání adresy — až s vyplněným PSČ (248: bez něj najde stejnou ulici
  // v jiném městě). Ručně zadané souřadnice ani uložené k nezměněné adrese nepřepisuje.
  const addrAuto = f ? joinAddress(f) : "";
  const zipOk = !!f && String(f.zip || "").replace(/\s/g, "").length >= 5;
  useEffect(() => {
    if (!open || !f) return undefined;
    const g = geoRef.current;
    if (g.manual || addrAuto.length < 3 || addrAuto === g.last || !zipOk) return undefined;
    const maCoords = parseNum(f.lat) !== null && parseNum(f.lng) !== null;
    if (maCoords && addrAuto === g.start) return undefined;
    const t = setTimeout(() => geocode(addrAuto, true), 1200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, addrAuto]);

  if (!f) return null;

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const dirty = initial.current !== JSON.stringify(f);
  const nameOk = f.name.trim().length > 0;

  const price = parseNum(f.contract_price);
  const budget = parseNum(f.planned_budget) || 0;
  const area = parseNum(f.area_m2);
  const marginText = price > 0
    ? `${(((price - budget) / price) * 100).toFixed(1).replace(".", ",")} % (${formatCZK(price - budget)})`
    : "doplň cenu díla";
  const perM2Text = price > 0 && area > 0 ? `${formatCZK(price / area)} / m²` : "doplň plochu";

  let durationText = "Doplň oba termíny.";
  if (f.start_date && f.end_date) {
    const d = Math.round((new Date(f.end_date) - new Date(f.start_date)) / 86400000);
    if (!isNaN(d)) durationText = d < 0
      ? "Dokončení je před zahájením — zkontroluj datum."
      : `Doba stavby ${d} ${dayWord(d)}.`;
  }

  const addrLine = joinAddress(f);
  const geoMsg = geoState === "ok" ? "✓ Souřadnice nalezeny podle adresy."
    : geoState === "auto" ? (geoHits.length > 1 ? "✓ Poloha doplněna podle adresy. Není to ono? Vyberte jinou níže."
      : "✓ Poloha doplněna podle adresy — zkontrolujte špendlík na mapě.")
    : geoState === "error" ? "Adresu se nepodařilo najít — zadej souřadnice ručně."
    : !zipOk ? "Doplň PSČ — poloha se pak dohledá sama."
    : "Bez souřadnic deník nedoplní počasí a stavba se neukáže na mapě.";
  const geoCls = geoState === "ok" || geoState === "auto" ? "text-success" : geoState === "error" ? "text-destructive" : "text-muted-foreground";

  const applyHit = (h) => { setF((p) => ({ ...p, lat: h.lat, lng: h.lng })); setGeoHits([]); setGeoState("ok"); };

  const findGps = () => {
    if (addrLine.length < 3) return toast.error("Nejdřív vyplňte ulici nebo město.");
    setGeoState("idle");
    return geocode(addrLine, false);
  };
  const setCoord = (k) => (e) => { geoRef.current.manual = true; set(k)(e); };

  const onCover = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try { const url = await uploadFile(file); setF((p) => ({ ...p, cover_photo: url })); }
    catch (err) { toast.error(apiError(err)); }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (!nameOk) return toast.error("Vyplňte název.");
    const lat = parseNum(f.lat), lng = parseNum(f.lng);
    if ((lat === null) !== (lng === null)) return toast.error("Vyplňte obě souřadnice, nebo žádnou.");
    if (lat !== null && (lat < -90 || lat > 90 || lng < -180 || lng > 180))
      return toast.error("Souřadnice jsou mimo rozsah (šířka −90…90, délka −180…180).");
    const payload = {
      name: f.name.trim(), code: f.code || null,
      street: f.street.trim() || null, zip: f.zip.trim() || null, city: f.city.trim() || null,
      address: addrLine || null,   // server si ji stejně přepočítá z částí
      lat, lng,
      contract_price: parseNum(f.contract_price), planned_budget: parseNum(f.planned_budget),
      area_m2: parseNum(f.area_m2),
      start_date: f.start_date || null, end_date: f.end_date || null,
      description: f.description || null, cover_photo: f.cover_photo || null,
      site_manager_id: f.site_manager_id || null, investor_id: f.investor_id || null,
    };
    setBusy(true);
    try {
      if (mode === "create") {
        const { data } = await api.post("/projects", { ...payload, status: f.status || "Poptávka", team: addTeam ? eligibleIds : [] });
        toast.success("Zakázka vytvořena.");
        onCreated?.(data);
        onOpenChange(false);
        return;
      }
      await api.patch(`/projects/${projectId}`, buildPayload ? buildPayload(payload) : payload);
      initial.current = JSON.stringify(f);
      setSaved(true);
      toast.success("Zakázka upravena.");
      onSaved?.();
      // Zelené „Uloženo" ať je vidět, teprve pak se dialog zavře.
      setTimeout(() => onOpenChange(false), 900);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  // Zavření s rozdělanými změnami se musí potvrdit (křížek, Esc i klik mimo).
  const handleOpenChange = async (next) => {
    if (next) return onOpenChange(true);
    if (dirty && !saved && !await ask("Máš neuložené změny. Zahodit?", { ok: "Zahodit změny", danger: true })) return;
    onOpenChange(false);
  };

  const chip = saved ? { t: "✓ Uloženo", c: "bg-success/15 text-success" }
    : dirty ? { t: "Neuložené změny", c: "bg-amber-500/15 text-amber-600 dark:text-amber-400" }
    : { t: "Beze změn", c: "bg-secondary text-muted-foreground" };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent data-testid="edit-project-dialog"
        onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === "Enter") { e.preventDefault(); save(); } }}
        className="max-w-[860px] w-[calc(100%-1.5rem)] sm:w-full p-0 gap-0 flex flex-col max-h-[92vh] overflow-hidden">

        {/* Hlavička — nescrolluje */}
        <div className="shrink-0 px-5 md:px-6 py-4 border-b border-border flex items-start justify-between gap-3 pr-14">
          <div className="min-w-0">
            <DialogTitle className="text-xl md:text-2xl font-black tracking-tight">{mode === "create" ? "Nová zakázka" : "Upravit zakázku"}</DialogTitle>
            <p className="text-sm text-muted-foreground truncate mt-0.5">
              {mode === "create" ? "Povinný je jen název — zbytek jde doplnit kdykoli." : [proj?.code, proj?.name].filter(Boolean).join(" · ")}
            </p>
          </div>
          <span data-testid="edit-dirty-chip"
            className={`rounded-full px-3 py-1.5 text-[13px] font-bold whitespace-nowrap shrink-0 ${chip.c}`}>{chip.t}</span>
        </div>

        {/* Tělo — jediné, co scrolluje */}
        <div className="flex-1 overflow-y-auto px-5 md:px-6 py-5 space-y-4 min-w-0">

          <Sec icon={Building2} title="Základní údaje">
            <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr] gap-3.5">
              <div>
                <Label>Název <span className="text-brand">*</span></Label>
                <Input data-testid="edit-name" value={f.name} onChange={set("name")} autoFocus
                  className={`h-12 mt-1 w-full min-w-0 text-base font-semibold ${nameOk ? "" : "border-destructive"}`} />
                {!nameOk && <p className="text-[13px] text-destructive mt-1">Název je povinný.</p>}
              </div>
              <div>
                <Label>Interní kód</Label>
                <Input data-testid="edit-code" value={f.code} onChange={set("code")} className="h-12 mt-1 w-full min-w-0" />
              </div>
            </div>
            {mode === "create" && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                  <div>
                    <Label>Stav</Label>
                    <Select value={f.status || "Poptávka"} onValueChange={(v) => setF((p) => ({ ...p, status: v }))}>
                      <SelectTrigger className="h-12 mt-1" data-testid="create-status"><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <label className="flex items-start gap-3 rounded-[12px] border border-border p-3.5 cursor-pointer" data-testid="create-add-team-row">
                  <Checkbox checked={addTeam} onCheckedChange={(c) => setAddTeam(!!c)} data-testid="create-add-team" className="mt-0.5" />
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold">Přidat celý tým firmy ({eligibleIds.length} lidí)</span>
                    <span className="block text-xs text-muted-foreground mt-0.5">Všichni dělníci, subdodavatelé a stavbyvedoucí uvidí zakázku a můžou na ní evidovat docházku. Tým jde upravit kdykoli později.</span>
                  </span>
                </label>
              </>
            )}
            <div>
              <Label>Popis</Label>
              <Textarea value={f.description} onChange={set("description")} rows={2}
                placeholder="Krátce, o co na stavbě jde…" className="mt-1 w-full min-w-0" />
            </div>
          </Sec>

          <Sec icon={MapPin} title="Místo stavby" testid="edit-place" action={
            <Button type="button" variant="outline" onClick={findGps} disabled={geoBusy || addrLine.length < 3}
              className="h-10 shrink-0 font-semibold" data-testid="edit-geocode">
              {geoBusy ? "Hledám…" : geoState === "idle" ? "Najít GPS" : "Hledat znovu"}
            </Button>
          }>
            <div className="grid grid-cols-1 sm:grid-cols-[2fr_1fr_1.4fr] gap-3.5">
              {/* Nápověda je obecný příklad, ne adresa nikoho z firmy — jinak to
                  vypadá jako předvyplněná hodnota. */}
              <div>
                <Label>Ulice a č. p.</Label>
                <Input data-testid="edit-street" value={f.street} onChange={set("street")}
                  placeholder="Václavské náměstí 1" className="h-12 mt-1 w-full min-w-0" />
              </div>
              <div>
                <Label>PSČ</Label>
                <Input data-testid="edit-zip" value={f.zip} onChange={set("zip")} inputMode="numeric"
                  placeholder="110 00" className="h-12 mt-1 w-full min-w-0 tabular-nums" />
              </div>
              <div>
                <Label>Město</Label>
                <Input data-testid="edit-city" value={f.city} onChange={set("city")}
                  placeholder="Praha 1" className="h-12 mt-1 w-full min-w-0" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
              <div>
                <Label className="text-muted-foreground">Zeměpisná šířka</Label>
                <Input data-testid="edit-lat" value={f.lat} onChange={setCoord("lat")} inputMode="decimal"
                  placeholder="50.081290" className="h-12 mt-1 w-full min-w-0 tabular-nums" />
              </div>
              <div>
                <Label className="text-muted-foreground">Zeměpisná délka</Label>
                <Input data-testid="edit-lng" value={f.lng} onChange={setCoord("lng")} inputMode="decimal"
                  placeholder="14.428000" className="h-12 mt-1 w-full min-w-0 tabular-nums" />
              </div>
            </div>

            {geoHits.length > 0 && (
              <div className="rounded-[12px] border border-border divide-y divide-border overflow-hidden min-w-0"
                data-testid="edit-geocode-hits">
                {geoHits.map((h, i) => (
                  <button key={i} type="button" onClick={() => applyHit(h)} title={h.label}
                    aria-pressed={parseNum(f.lat) === h.lat && parseNum(f.lng) === h.lng}
                    className="block w-full min-w-0 text-left px-3.5 py-2.5 text-sm hover:bg-secondary transition-colors aria-pressed:bg-brand/10 aria-pressed:font-semibold">
                    {/* break-words, NE truncate: nezalomitelný řádek nafoukne mřížku dialogu */}
                    <span className="block break-words">{h.short || h.label}</span>
                    <span className="block text-xs text-muted-foreground tabular-nums">{h.lat}, {h.lng}</span>
                  </button>
                ))}
              </div>
            )}

            <p className={`text-[13px] ${geoCls}`} data-testid="edit-geo-msg">{geoMsg}</p>

            {mapPos ? (
              <div className="rounded-[14px] border border-border overflow-hidden">
                {/* MapView si nese vlastní rámeček a rádius — uvnitř kontejneru je zbytečný */}
                <div className="[&>div]:!rounded-none [&>div]:!border-0">
                  <MapView lat={mapPos.lat} lng={mapPos.lng} height={260} zoom={17} />
                </div>
                <div className="flex items-center justify-between gap-3 px-3.5 py-2.5 border-t border-border">
                  <span className="text-[13px] text-muted-foreground truncate">{addrLine || "Adresa nevyplněna"}</span>
                  <a href={`https://www.openstreetmap.org/?mlat=${mapPos.lat}&mlon=${mapPos.lng}#map=17/${mapPos.lat}/${mapPos.lng}`}
                    target="_blank" rel="noreferrer" data-testid="edit-map-link"
                    className="text-sm font-bold text-brand hover:underline whitespace-nowrap">Otevřít v mapách ↗</a>
                </div>
              </div>
            ) : (
              <div className="h-[200px] sm:h-[260px] rounded-[14px] border border-border bg-card grid place-items-center px-4 text-center text-sm text-muted-foreground">
                {geoBusy ? "Hledám polohu…" : "Zadej adresu — poloha se najde sama."}
              </div>
            )}
          </Sec>

          <Sec icon={Wallet} title="Peníze a termíny">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
              {[["contract_price", `Cena díla (${vatWord})`, currencySymbol(), "edit-price"],
                ["planned_budget", `Rozpočet (${vatWord})`, currencySymbol(), "edit-budget"],
                ["area_m2", "Plocha", "m²", "edit-area"]].map(([k, label, unit, tid]) => (
                <div key={k}>
                  <Label>{label}</Label>
                  <div className="relative mt-1">
                    <Input data-testid={tid} value={f[k]} onChange={set(k)} inputMode="decimal"
                      className="h-12 w-full min-w-0 pr-12 tabular-nums" />
                    <span className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-[15px] font-semibold text-muted-foreground">{unit}</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="rounded-[12px] bg-card border border-border px-3.5 py-3 text-sm text-muted-foreground" data-testid="edit-calc">
              Marže z těchto čísel: <strong className="text-foreground whitespace-nowrap">{marginText}</strong>
              {" · "}cena za m²: <strong className="text-foreground whitespace-nowrap">{perM2Text}</strong>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
              <div>
                <Label>Zahájení</Label>
                <Input type="date" data-testid="edit-start" value={f.start_date} onChange={set("start_date")} className="h-12 mt-1 w-full min-w-0" />
              </div>
              <div>
                <Label>Dokončení</Label>
                <Input type="date" data-testid="edit-end" value={f.end_date} onChange={set("end_date")} className="h-12 mt-1 w-full min-w-0" />
              </div>
            </div>
            <p className="text-[13px] text-muted-foreground" data-testid="edit-duration">{durationText}</p>
          </Sec>

          <Sec icon={Users} title="Lidé a fotka">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
              <div>
                <Label>Stavbyvedoucí</Label>
                <Select value={f.site_manager_id || "none"} onValueChange={(v) => setF((p) => ({ ...p, site_manager_id: v === "none" ? "" : v }))}>
                  <SelectTrigger className="h-12 mt-1" data-testid="edit-site-manager"><SelectValue placeholder="Nepřiřazeno" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nepřiřazeno</SelectItem>
                    {(team || []).filter((m) => m.role === "OWNER" || m.role === "MANAGER").map((m) => (
                      <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Investor</Label>
                <Select value={f.investor_id || "none"} onValueChange={(v) => setF((p) => ({ ...p, investor_id: v === "none" ? "" : v }))}>
                  <SelectTrigger className="h-12 mt-1" data-testid="edit-investor"><SelectValue placeholder="Nepřiřazeno" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nepřiřazeno</SelectItem>
                    {(investors || []).map((inv) => (
                      <SelectItem key={inv.id} value={inv.id}>{inv.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {(investors || []).length === 0 && (
                  <p className="text-[13px] text-muted-foreground mt-1">Zatím žádní investoři — přidejte je v sekci Investoři.</p>
                )}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3.5">
              {imgReady && f.cover_photo
                ? <img src={resolveImg(f.cover_photo, variantWidth(88))} alt="" className="w-[88px] h-16 object-cover rounded-[12px] border border-border shrink-0" />
                : <div className="w-[88px] h-16 rounded-[12px] border border-border bg-secondary grid place-items-center text-muted-foreground shrink-0"><ImageIcon size={18} /></div>}
              <div className="min-w-0 flex-1">
                <div className="text-[15px] font-bold">Cover fotka</div>
                <div className="text-[13px] text-muted-foreground">Zobrazí se v seznamu zakázek a v hlavičce detailu.</div>
              </div>
              <input ref={coverRef} type="file" accept="image/*" onChange={onCover} className="hidden" data-testid="cover-input" />
              <Button type="button" variant="outline" onClick={() => coverRef.current?.click()} disabled={uploading}
                className="h-11 w-full sm:w-auto font-bold shrink-0" data-testid="cover-upload-btn">
                <Upload size={16} /> {uploading ? "Nahrávám…" : f.cover_photo ? "Nahrát jinou" : "Nahrát fotku"}
              </Button>
            </div>
          </Sec>
        </div>

        {/* Patička — nescrolluje */}
        <div className="shrink-0 px-5 md:px-6 py-3.5 border-t border-border flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <p className="text-[13px] text-muted-foreground">{dirty ? "Změny se uloží až tlačítkem." : "Nic zatím neměníš."}</p>
            {mode !== "create" && ["OWNER", "ASSISTANT"].includes(user?.role) && (
              <button type="button" onClick={openDelete} data-testid="delete-project-btn"
                className="mt-1 inline-flex items-center gap-1 text-[13px] font-bold text-destructive hover:underline">
                <Trash2 size={13} /> Smazat zakázku
              </button>
            )}
          </div>
          <div className="flex flex-col-reverse sm:flex-row gap-2">
            <Button variant="outline" onClick={() => handleOpenChange(false)} className="h-11 w-full sm:w-auto font-bold" data-testid="edit-cancel">Zrušit</Button>
            <Button onClick={save} disabled={busy || !nameOk} data-testid="edit-save"
              className={`h-11 w-full sm:w-auto font-bold ${saved ? "bg-success hover:bg-success/90 text-white" : ""}`}>
              {saved ? <><Check size={16} /> Uloženo</> : busy ? "Ukládám…" : mode === "create" ? "Vytvořit zakázku" : "Uložit změny"}
            </Button>
          </div>
        </div>
      </DialogContent>

      {/* Smazání zakázky — výstraha s reálnými počty + opsání názvu */}
      <Dialog open={delOpen} onOpenChange={setDelOpen}>
        <DialogContent className="max-w-md" data-testid="delete-project-dialog">
          <DialogTitle className="flex items-center gap-2 text-destructive"><AlertTriangle size={20} /> Smazat zakázku?</DialogTitle>
          {delInfo && (
            <div className="space-y-3">
              <p className="text-sm leading-relaxed">Zakázka <b>{delInfo.name}</b> se přesune do koše a za <b>{delInfo.trashDays} dní se smaže definitivně</b> — včetně všech navázaných dat. Tohle zmizí:</p>
              <ul className="text-sm rounded-[12px] border border-destructive/30 bg-destructive/5 p-3 space-y-1">
                <li>• {delInfo.counts.tasks} úkolů</li>
                <li>• {delInfo.counts.attendance} směn docházky</li>
                <li>• {delInfo.counts.diary} zápisů deníku a {delInfo.counts.photos} fotek</li>
                <li>• {delInfo.counts.files} dokumentů a {delInfo.counts.phases} fází harmonogramu</li>
                <li>• {delInfo.counts.invoices} přijatých faktur a {delInfo.counts.receipts} účtenek</li>
              </ul>
              <p className="text-[13px] text-muted-foreground leading-relaxed">
                Faktura rozdělená mezi víc zakázek se nesmaže celá — odpojí se z ní jen podíl této zakázky.
                {(delInfo.counts.issued || 0) > 0 && <> Vydané faktury ({delInfo.counts.issued}) se nesmažou — zůstanou v Ekonomice bez zakázky.</>}
                Skutečnou hotovou zakázku nemaž — použij stav <b>Dokončeno</b>.
              </p>
              <div>
                <Label>Pro potvrzení opiš název zakázky</Label>
                <Input value={delName} onChange={(e) => setDelName(e.target.value)} placeholder={delInfo.name}
                  className="h-11 mt-1" data-testid="delete-project-name" />
              </div>
              <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
                <Button variant="outline" onClick={() => setDelOpen(false)} className="h-11 w-full sm:w-auto font-bold">Zpět</Button>
                <Button onClick={confirmDelete} disabled={delBusy || delName.trim() !== (delInfo.name || "").trim()} data-testid="delete-project-confirm"
                  className="h-11 w-full sm:w-auto font-bold bg-destructive hover:bg-destructive/90 text-white disabled:opacity-40">
                  <Trash2 size={16} /> {delBusy ? "Mažu…" : "Přesunout do koše"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
